// Packing slip rendering — the single source of truth.
//
// This markup used to be copy-pasted in ShippingTab.js and OrdersTab.js, which
// meant every fix (discount line, store credit line) had to be made twice and
// the two drifted anyway. Everything here is pure string building so it can be
// tested without a DOM; printPackingSlips owns the iframe printing lifecycle.
//
// Batch printing is the reason this is a document builder rather than a
// one-slip builder: N slips go into ONE document separated by page breaks, so
// the operator prints once instead of dismissing N print dialogs.

/** HTML-escape — customer names/addresses are untrusted free text. */
export const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Display-only size enrichment; never changes the stored order item. */
export function lineDisplayName(item, productsById = {}) {
  const name = item.name || "";
  if (/\d+(?:\.\d+)?\s*(?:mg|mcg|mL|ct)\b/i.test(name)) return name;
  if (item.option_label?.trim()) return `${name} ${item.option_label.trim()}`;

  const subtitle = productsById[item.product_id]?.subtitle || "";
  // Keep both package count and per-tablet strength: "100ct · 10mg".
  const sizeToken = String.raw`\d+(?:\.\d+)?\s*(?:mg|mcg|mL|ct)\b(?:\s*\/(?:\s*\d+(?:\.\d+)?\s*)?(?:mg|mcg|mL|ct)\b)?`;
  const size = subtitle.match(new RegExp(`^\\s*(${sizeToken}(?:\\s*·\\s*${sizeToken})*)`, "i"))?.[1];
  if (size) return `${name} ${size}`;

  // Unknown product or missing/unrecognized size: do not invent a strength.
  return name;
}

/**
 * Total UNITS in the order, not line count: three lines of qty 2/1/4 is 7 items.
 * Alex packs from this slip and was missing items, so the slip has to state the
 * number he should be able to count in the box. A missing qty counts as 1, the
 * same rule slipTotals prices it by, so the count and the money never disagree.
 */
export function slipUnitCount(slip = {}) {
  return (slip.items || []).reduce((n, i) => n + (Number(i.qty) || 1), 0);
}

/**
 * Money math for one slip. Kept separate from markup so the numbers are
 * testable on their own.
 *
 * `total` (the recorded order total) wins when present — the slip must agree
 * with what the customer was actually charged, even if line items were edited
 * after the fact. Only when it is absent do we recompute.
 */
export function slipTotals(slip = {}) {
  const items = slip.items || [];
  const subtotal = items.reduce((sum, i) => sum + num(i.price) * (i.qty || 1), 0);
  const shipping = num(slip.shipping);
  const bulkDiscount = num(slip.bulkDiscount);
  const discount = num(slip.discount);
  const storeCredit = num(slip.storeCredit);
  const grandTotal = slip.total != null && slip.total !== ""
    ? num(slip.total)
    : subtotal - bulkDiscount - discount - storeCredit + shipping;
  return { subtotal, shipping, bulkDiscount, discount, storeCredit, grandTotal };
}

export const PACKING_SLIP_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: 4in 6in; margin: 0; }
  body { font-family: -apple-system, sans-serif; font-size: 11px; color: #111; }
  .slip { width: 4in; height: 6in; padding: 12px 14px; overflow: hidden; }
  .slip + .slip { page-break-before: always; }
  h1 { font-size: 14px; margin-bottom: 2px; }
  .meta { color: #666; font-size: 10px; margin-bottom: 10px; }
  .section { margin-bottom: 8px; }
  .label { font-size: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; margin-bottom: 2px; }
  .addr { line-height: 1.4; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th { text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; padding: 3px 0; border-bottom: 1px solid #ddd; }
  td { padding: 4px 0; border-bottom: 1px solid #eee; font-size: 10px; }
  .r { text-align: right; }
  .totals td { border-bottom: none; padding: 2px 0; font-size: 10px; }
  .totals .grand { font-weight: 700; font-size: 11px; border-top: 1px solid #ddd; padding-top: 4px; }
  .footer { margin-top: 10px; padding-top: 6px; border-top: 1px solid #ddd; font-size: 9px; color: #888; }
  .headcount { margin: 6px 0 2px; padding: 5px 8px; border: 1.5px solid #111; border-radius: 3px; display: flex; align-items: baseline; justify-content: space-between; }
  .headcount .n { font-size: 20px; font-weight: 800; line-height: 1; }
  .headcount .t { font-size: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #555; }
  @media print { .slip { padding: 10px 12px; } }
`;

/**
 * One slip's markup. `slip` fields:
 *   orderId, orderDate, toName, toStreet, toStreet2, toCity, toState, toZip,
 *   fromName, fromStreet, fromCity, fromState, fromZip,
 *   items[{name, qty, price}], shipping, total, bulkDiscount, couponCode, discount, storeCredit
 */
export function packingSlipHtml(slip = {}) {
  const items = slip.items || [];
  const date = slip.orderDate
    ? new Date(slip.orderDate).toLocaleDateString(undefined, { dateStyle: "medium" })
    : "";
  const { subtotal, shipping, bulkDiscount, discount, storeCredit, grandTotal } = slipTotals(slip);
  const units = slipUnitCount(slip);

  return `<div class="slip">
<h1>Packing Slip</h1>
<p class="meta">Order ${esc(slip.orderId)}${date ? ` — ${esc(date)}` : ""}</p>
<div class="section">
  <p class="label">Ship To</p>
  <div class="addr">
    <strong>${esc(slip.toName)}</strong><br>
    ${esc(slip.toStreet)}${slip.toStreet2 ? `<br>${esc(slip.toStreet2)}` : ""}<br>
    ${esc(slip.toCity)}, ${esc(slip.toState)} ${esc(slip.toZip)}
  </div>
</div>
<div class="section">
  <p class="label">From</p>
  <div class="addr">
    ${esc(slip.fromName)}<br>
    ${esc(slip.fromStreet)}<br>
    ${esc(slip.fromCity)}, ${esc(slip.fromState)} ${esc(slip.fromZip)}
  </div>
</div>
<div class="section">
  <p class="label">Items</p>
  <div class="headcount">
    <span class="n">${units}</span>
    <span class="t">${units === 1 ? "item" : "items"} to pack &mdash; count the box</span>
  </div>
  <table>
    <tr><th>Product</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Total</th></tr>
    ${items.map(i => {
      const lineTotal = num(i.price) * (i.qty || 1);
      return `<tr><td>${esc(i.name)}</td><td class="r">${esc(i.qty)}</td><td class="r">$${num(i.price).toFixed(2)}</td><td class="r">$${lineTotal.toFixed(2)}</td></tr>`;
    }).join("")}
  </table>
  <table class="totals" style="margin-top: 6px;">
    <tr><td>Subtotal</td><td class="r">$${subtotal.toFixed(2)}</td></tr>
    ${bulkDiscount > 0 ? `<tr><td>10-pack discount</td><td class="r">−$${bulkDiscount.toFixed(2)}</td></tr>` : ""}
    ${discount > 0 ? `<tr><td>Discount${slip.couponCode ? ` (${esc(slip.couponCode)})` : ""}</td><td class="r">−$${discount.toFixed(2)}</td></tr>` : ""}
    ${storeCredit > 0 ? `<tr><td>Store Credit</td><td class="r">−$${storeCredit.toFixed(2)}</td></tr>` : ""}
    <tr><td>Shipping</td><td class="r">${shipping === 0 ? "FREE" : "$" + shipping.toFixed(2)}</td></tr>
    <tr class="grand"><td>Total</td><td class="r">$${grandTotal.toFixed(2)}</td></tr>
  </table>
</div>
<div class="footer">Thank you for your order!</div>
</div>`;
}

/**
 * One printable document holding every slip. `.slip + .slip` carries the page
 * break, so N slips print as N pages from a single print dialog.
 */
export function packingSlipDocument(slips = []) {
  const list = Array.isArray(slips) ? slips : [slips];
  const title = list.length === 1 ? "Packing Slip" : `Packing Slips (${list.length})`;
  return `<!DOCTYPE html><html><head><title>${esc(title)}</title>
<style>${PACKING_SLIP_STYLES}</style></head><body>
${list.map(packingSlipHtml).join("\n")}

</body></html>`;
}

/** Print same-origin HTML in an offscreen iframe, without opening a tab.
 * The document is created synchronously; only loading/printing waits.
 * Resolves false on setup, load or print failure, so callers can report it.
 */
export function printPackingSlips(slips, {
  document: doc = globalThis.document,
  loadTimeout = 5000,
  cleanupDelay = 120000,
} = {}) {
  const list = (Array.isArray(slips) ? slips : [slips]).filter(Boolean);
  if (!list.length) return Promise.resolve(false);
  return new Promise(resolve => {
    let iframe, loadTimer, cleanupTimer, printWindow;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      clearTimeout(loadTimer);
      clearTimeout(cleanupTimer);
      printWindow?.removeEventListener("afterprint", cleanup);
      iframe?.remove();
    };
    const fail = () => { cleanup(); resolve(false); };
    try {
      iframe = doc.createElement("iframe");
      iframe.title = "Packing slips";
      iframe.setAttribute("aria-hidden", "true");
      // Keep it rendered for mobile printing; display:none can produce blank pages.
      iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:4in;height:6in;border:0;";
      iframe.onload = () => {
        if (cleaned) return;
        iframe.onload = null;
        clearTimeout(loadTimer);
        try {
          printWindow = iframe.contentWindow;
          printWindow.addEventListener("afterprint", cleanup, { once: true });
          printWindow.print();
          // Some mobile browsers never dispatch afterprint.
          cleanupTimer = setTimeout(cleanup, cleanupDelay);
          resolve(true);
        } catch { fail(); }
      };
      iframe.onerror = fail;
      iframe.srcdoc = packingSlipDocument(list);
      loadTimer = setTimeout(fail, loadTimeout);
      doc.body.appendChild(iframe);
    } catch { fail(); }
  });
}

/** Single-slip convenience — the shape both admin tabs used to call. */
export const printPackingSlip = (slip) => printPackingSlips([slip]);

/**
 * orders → slip inputs. One place that knows how an `orders` row plus its
 * `order_items` maps onto the slip fields, so Orders/Shipping/batch all agree.
 */
export function orderToSlip(order, fromConfig = {}, productsById = {}) {
  const addr = order.shipping_address || {};
  const items = order.order_items || order.items || [];
  return {
    orderId: order.id,
    orderDate: order.created_at || order.orderDate,
    toName: addr.name || order.profiles?.name || "",
    toStreet: addr.street || "",
    toStreet2: addr.street2 || "",
    toCity: addr.city || "",
    toState: addr.state || "",
    toZip: addr.zip || "",
    fromName: fromConfig.from_company || fromConfig.from_name || "",
    fromStreet: fromConfig.from_street || "",
    fromCity: fromConfig.from_city || "",
    fromState: fromConfig.from_state || "",
    fromZip: fromConfig.from_zip || "",
    items: items.map(i => ({ name: lineDisplayName(i, productsById), qty: i.qty, price: i.price })),
    shipping: order.shipping,
    total: order.total,
    bulkDiscount: order.bulk_vial_discount,
    couponCode: order.coupon_code,
    discount: order.coupon_discount,
    storeCredit: order.store_credit_applied,
  };
}
